/**
 * Hero passives — the MOBA resource layer (GDD hero section: "passive + Q/E/R").
 * Each fantasy hero has a 0–100 resource that charges from their signature
 * playstyle; a full meter automatically discharges as a **surge** that shortens
 * every running ability cooldown (`AbilityTracker.hasten`). A surge never
 * touches tuning, position or velocity, so the GDD balance rules (no checkpoint
 * teleports, no permanent boosts, no hazard immunity) hold by construction.
 */

/** Movement snapshot for one fixed tick, assembled by the game loop. */
export interface PassiveTickState {
  /** Fixed tick length in seconds (gain rates are per-second). */
  dt: number;
  /** Standing on walkable ground this tick. */
  grounded: boolean;
  /** Riding a steep ramp face this tick (airborne in Source terms). */
  surfing: boolean;
  /** Rising edge: was airborne last tick, grounded now. */
  justLanded: boolean;
  /** Fall speed (u/s, >= 0) the instant before a landing; 0 on other ticks. */
  landingFallSpeed: number;
  /** Horizontal speed (u/s) after the tick. */
  horizontalSpeed: number;
  /** Airborne + view turning + horizontal speed grew — one clean strafe tick. */
  strafeGain: boolean;
}

export interface PassiveDef {
  /** Stable id (for tests/telemetry). */
  id: string;
  /** Resource name shown on the HUD meter (e.g. 'Tempo'). */
  name: string;
  /** One-line description for the hero card / info panel. */
  description: string;
  /** Resource gained this tick on the 0–100 meter. Pure. */
  gain(state: PassiveTickState): number;
  /** Seconds shaved off every running cooldown when the meter fills. */
  surgeHasteSec: number;
}

export const PASSIVE_METER_MAX = 100;

// Feature-level pacing knobs (per-second rates / per-event chunks on the 0–100
// meter). Ideal play fills a meter in roughly 5–10 seconds — frequent enough to
// feel like a resource, rare enough that a surge stays an earned moment.
const TEMPO_RATE = 16; // ~6.3s of clean carving
const GRACE_PER_LANDING = 25; // 4 clean landings
const GRACE_FALL_SPEED_CAP = 420; // touch down softer than this = "clean"
const MOMENTUM_FLOOR_SPEED = 600;
const MOMENTUM_FULL_SPEED = 2000;
const MOMENTUM_MIN_RATE = 6;
const MOMENTUM_MAX_RATE = 20; // ~5s riding at full tilt
const SHADOW_RATE = 11; // ~9s of true flight
const SURGE_HASTE_SEC = 4;

/** Vaelith — Tempo: charged by airborne turns that convert into speed. */
export const TEMPO: PassiveDef = {
  id: 'tempo',
  name: 'Tempo',
  description:
    'Clean air strafes build Tempo; at full, surge — every cooldown ticks down 4s faster.',
  surgeHasteSec: SURGE_HASTE_SEC,
  gain: (s) => (s.strafeGain ? TEMPO_RATE * s.dt : 0),
};

/** Seraphine — Grace: charged by touching down softly. */
export const GRACE: PassiveDef = {
  id: 'grace',
  name: 'Grace',
  description: 'Clean landings build Grace; at full, surge — every cooldown shortens by 4s.',
  surgeHasteSec: SURGE_HASTE_SEC,
  gain: (s) => (s.justLanded && s.landingFallSpeed < GRACE_FALL_SPEED_CAP ? GRACE_PER_LANDING : 0),
};

/** Brakka — Momentum: charged by staying on ground/ramps at speed. */
export const MOMENTUM: PassiveDef = {
  id: 'momentum',
  name: 'Momentum',
  description:
    'Riding ground or ramps at speed builds Momentum; at full, surge — cooldowns shorten by 4s.',
  surgeHasteSec: SURGE_HASTE_SEC,
  gain: (s) => {
    if (!(s.grounded || s.surfing) || s.horizontalSpeed < MOMENTUM_FLOOR_SPEED) {
      return 0;
    }
    const t = Math.min(
      1,
      (s.horizontalSpeed - MOMENTUM_FLOOR_SPEED) / (MOMENTUM_FULL_SPEED - MOMENTUM_FLOOR_SPEED),
    );
    return (MOMENTUM_MIN_RATE + (MOMENTUM_MAX_RATE - MOMENTUM_MIN_RATE) * t) * s.dt;
  },
};

/** Nyx — Shadow: charged by true air time (off the ground AND off the ramps). */
export const SHADOW: PassiveDef = {
  id: 'shadow',
  name: 'Shadow',
  description: 'Air time builds Shadow; at full, surge — every cooldown shortens by 4s.',
  surgeHasteSec: SURGE_HASTE_SEC,
  gain: (s) => (!s.grounded && !s.surfing ? SHADOW_RATE * s.dt : 0),
};

/**
 * The fill/discharge state machine for the selected hero's passive. Fed once
 * per fixed tick; reset on respawn / hero switch / map switch (resources are
 * earned within a run, never carried across one).
 */
export class PassiveMeter {
  private valueInternal = 0;

  /** Current fill, 0..PASSIVE_METER_MAX. */
  get value(): number {
    return this.valueInternal;
  }

  /** 0..1 fill fraction for the HUD. */
  get fraction(): number {
    return this.valueInternal / PASSIVE_METER_MAX;
  }

  /**
   * Accumulate this tick's gain. Returns `true` on the tick the meter fills —
   * the surge edge; the meter resets to 0 (overflow is discarded: a surge is a
   * discrete earned moment, not a rolling bank).
   */
  tick(def: PassiveDef, state: PassiveTickState): boolean {
    this.valueInternal += Math.max(0, def.gain(state));
    if (this.valueInternal >= PASSIVE_METER_MAX) {
      this.valueInternal = 0;
      return true;
    }
    return false;
  }

  reset(): void {
    this.valueInternal = 0;
  }
}
